import Backbone from '@polleverywhere/backbone'
import constructActivity from '../../util/construct_activity'
import createActivityFromUrl from '../../util/create_activity_from_url'
import normalizeHashRoutes from '../../util/normalize_hash_routes'
import getParentMQA from '../../util/get_parent_mqa'
import Skeleton from '../../models/skeleton'
import ErrorModel from '../../models/error'
import JoinInstructions from '../../models/join_instructions'
import Group from '@polleverywhere/core/lib/models/group'
import { activitiesToClear } from '@polleverywhere/slideware-bindings'
import openApplicationTroubleshoot from '../../util/open_application_troubleshoot'
import { slice as appSlice } from '../../slices/app'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import store from '@polleverywhere/redux'

async function getActivity (type, permalink, page) {
  // Render a skeleton. It can take a bit to fetch the activity in question
  this.state.appStates.slideware.set({
    activity: new Skeleton(),
    page: 'edit'
  })

  store.dispatch(appSlice.pageUpdated('slideware'))

  let activity = await constructActivity(type, permalink)

  // If there was an error in fetching, let the activities app show a message
  if (activity instanceof ErrorModel) {
    this.state.appStates.activities.changePage('group', {
      activity: activity,
      group: this.state.get('user').groups.get(-1)
    })
    store.dispatch(appSlice.pageUpdated('activities'))
    return
  }

  // If the user doesn't own the poll, let the activities app handle it
  // by showing a view with the 'copy activity' button
  if (activity.presenter.id !== store.getState()[profileSlice.name].user.id) {
    this.state.appStates.activities.changePage('group', {
      activity: activity,
      group: new Group({ id: -2, name: 'My activities' })
    })
    store.dispatch(appSlice.pageUpdated('activities'))
    return
  }

  // If the activity is part of an MQA, act on the parent MQA instead
  let selected
  const parentMQA = getParentMQA(activity)
  if (parentMQA) {
    selected = activity
    activity = await constructActivity(parentMQA.type, parentMQA.permalink)
  }

  this.state.appStates.slideware.set({
    activity,
    page
  }, { selected })
}

export default class Router extends Backbone.Router {
  get routes () {
    return {
      activities: () => {
        updateEnabledFeatures(this)
      },
      'join_instructions/sync': async () => {
        this.state.appStates.slideware.set({
          page: 'sync',
          activity: new JoinInstructions({
            owner_id: store.getState()[profileSlice.name].user.id,
            title: 'Join instructions'
          })
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      ':type/:permalink/sync': async (type, permalink) => {
        const activity = await constructActivity(
          type,
          permalink,
          { fetch: false }
        )
        let page = 'sync'

        if (activity instanceof ErrorModel) {
          activity.message = 'Something went wrong when we tried to sync your activity!  Please try re-inserting.'
          page = 'error'
        }

        this.state.appStates.slideware.set({
          page,
          activity
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      background: () => {
        this.state.appStates.slideware.set({
          page: 'background'
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      remote_event_driven: () => {
        this.state.appStates.slideware.set({
          page: 'remote_event_driven'
        })
        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      screenshots: () => {
        updateEnabledFeatures(this)
        this.state.appStates.slideware.set({
          insertionType: 'images'
        })

        store.dispatch(appSlice.pageUpdated('activities'))
      },
      syncall: () => {
        this.state.appStates.slideware.set({
          page: 'syncAll'
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      contact_support: () => {
        // TODO: This route is deprecated but we need to keep it until older app
        // versions are officially sunsetted.
        openApplicationTroubleshoot(this.state)
        store.dispatch(appSlice.pageUpdated('activities'))
      },
      'webpage/new': () => {
        this.state.appStates.slideware.set({
          page: 'insertWebPage'
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      'video/new': () => {
        this.state.appStates.slideware.set({
          page: 'insertVideo'
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      'activities/new': () => {
        updateEnabledFeatures(this)
        const params = new URL(normalizeHashRoutes(document.URL)).searchParams
        if (params.has('poll_title') || params.has('answers')) {
          this.state.appStates.slideware.set({
            activity: createActivityFromUrl(params),
            page: 'edit',
            pageAction: 'convert'
          })
          store.dispatch(appSlice.pageUpdated('slideware'))
          return
        }
        store.dispatch(appSlice.pageUpdated('activities'))
        this.state.appStates.activities.set({
          pageAction: 'create',
          group: this.state.get('user').groups.get(-1)
        })
      },
      join_instructions: async () => {
        this.state.appStates.slideware.set({
          page: 'joinInstructions',
          activity: new JoinInstructions({
            owner_id: store.getState()[profileSlice.name].user.id,
            title: 'Join instructions'
          })
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      modal: () => {
        const params = new URL(normalizeHashRoutes(document.URL)).searchParams
        this.state.appStates.slideware.set({
          page: 'modal'
        })

        // Support old modal messages for
        // backwards compat
        if (params.get('message')) {
          this.state.appStates.slideware.set({
            message: params.get('message')
          })
        }

        if (params.get('error_code')) {
          this.state.appStates.slideware.set({
            errorCode: params.get('error_code')
          })
        }

        store.dispatch(appSlice.pageUpdated('slideware'))
      },
      ':type/:permalink/edit': async (type, permalink) => {
        await getActivity.call(this, type, permalink, 'edit')
      },
      ':type/:permalink/flow': async (type, permalink) => {
        await getActivity.call(this, type, permalink, 'flow')
      },
      ':type/:permalink/clear_responses': async (type, permalink) => {
        await getActivity.call(this, type, permalink, 'clearResponsesArchiveNamePrompt')
      },
      clear_responses: async () => {
        const activities = await activitiesToClear()

        this.state.appStates.slideware.set({
          activity: activities,
          page: activities.length ? 'clearResponsesArchiveNamePrompt' : 'clearResponsesNoActivities'
        })

        store.dispatch(appSlice.pageUpdated('slideware'))
      }
    }
  }

  set routes (routes) {}

  initialize (options = {}) {
    this.state = options.state
  }
}

function updateEnabledFeatures (self) {
  const params = new URL(normalizeHashRoutes(document.URL)).searchParams
  let disabledFeatures = params.get('disable') || ''
  disabledFeatures = disabledFeatures.split(',')
  self.state.appStates.slideware.set({
    // Goog_presenter shows 'inserting' UI, which is indicated by a progress bar.
    // In the future, we are going to remove this an send inserting progress back. (Pivotal: #166997437)
    showInsertComplete: !disabledFeatures.includes('progress_bar')
  })
}
